import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import BusinessLoader from "../../../common/BusinessLoader";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { handleApi } from "../../../../apis";
import { GETWALLETVOUCHERPAYMENT } from "../../../../apis/configuration";
import { downloadReceiptImage, printWalletReceiptPreview, copyReceiptImage } from "../../../../utils";
import UCashTransactionSuccess from "../../../common/UCashTransactionSuccess";
import UCashTransactionReceipt from "../../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../../common/DownloadReceipt";
import { formatNum } from "../../../../oscar-pos-core/constants";

const VouchersTransactionSuccess = () => {
  const [I18n] = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [transactionDetail, setTransactionDetail] = useState({});
  const [state, setState] = useState({
    amount: 0,
    logo: "",
    date: null,
  });

  const receiptRef = useRef();
  const timeoutId = useRef(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { business } = useAuthContext();

  useEffect(() => {
    fetchWalletVoucherTransaction();
    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId.current);
    };
  }, []);

  const fetchWalletVoucherTransaction = async () => {
    if (location?.state?.data && location?.state?.transaction) {
      setState({ ...location.state.data });
      setTransactionDetail({ ...location.state.transaction });
      return;
    }
    setIsLoading(true);
    try {
      const response = await handleApi(
        GETWALLETVOUCHERPAYMENT,
        { job_id: params?.id },
        null,
        navigate,
      );
      if (!isMounted.current) return;
      if (response?.status === "COMPLETED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        setIsLoading(false);
        const transaction_details = response?.transaction_details || {};
        setTransactionDetail(transaction_details);
        setState({
          amount: Math.abs(
            transaction_details?.amount || response?.amount || 0,
          ),
          logo: transaction_details?.icon || response?.icon || "",
          date: transaction_details?.created_at,
        });
      } else if (response?.status === "FAILED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.voucher_transaction_failed,
          severity: "error",
          icon: "",
        });
        navigate("/UCash");
      } else if (isMounted.current) {
        setIsLoading(true);
        timeoutId.current = setTimeout(fetchWalletVoucherTransaction, 5000);
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (timeoutId.current) clearTimeout(timeoutId.current);
      showSnackbar({
        message:
          error?.response?.data?.message ||
          I18n.error_fetching_voucher_transaction,
        severity: "error",
        icon: "",
      });
      setIsLoading(false);
      navigate("/UCash");
    }
  };

  const handleDownload = () =>
    downloadReceiptImage(receiptRef, {
      setIsDownloadLoading,
      analyticsEvent: "w_earn_vouchers_download",
      fileName: "Rupin Voucher Receipt",
    });

  const handleCopy = () =>
    copyReceiptImage(receiptRef, {
      showSnackbar,
      I18n,
      analyticsEvent: "w_earn_vouchers_copy_receipt",
    });

  const tid = transactionDetail?.id || params?.id || "";
  const customerName = transactionDetail?.customer_name || "";
  const voucherName =
    transactionDetail?.voucher_name || transactionDetail?.voucher || "";
  const voucherLogo = transactionDetail?.logo_link || state.logo || "";
  const formattedDate = state.date
    ? moment(state.date).format("D MMMM YYYY, hh:mm A")
    : "";

  // Whole phrase from one key so each language sets its own order ("{name} کو واؤچر" in Urdu).
  // Direction comes from the template (the Urdu line starts with a Latin name, so dir="auto"
  // would wrongly pick left-to-right).
  const voucherTo = I18n.voucher_to || "Voucher to {name}";
  const [beforeName, afterName = ""] = voucherTo.split("{name}");
  const subtitleDir = /[\u0600-\u06FF]/.test(voucherTo) ? "rtl" : "ltr";
  const successSubtitle = (
    <span
      dir={subtitleDir}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 4 }}
    >
      <span>
        {customerName ? (
          <>
            {beforeName}
            <strong>{customerName}</strong>
            {afterName}
          </>
        ) : (
          `${I18n.voucher} ${I18n.to_lowercase}`
        )}
      </span>
      {voucherLogo ? (
        <span style={{
          display: "inline-flex", width: 28, height: 28,
          borderRadius: 6, flexShrink: 0, overflow: "hidden",
          alignItems: "center", justifyContent: "center",
        }}>
          <img src={voucherLogo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </span>
      ) : null}
      {voucherName ? <strong>{voucherName}</strong> : null}
    </span>
  );

  const receiptRows = [
    { label: I18n.by || "By", value: business?.name || "-" },
    { label: I18n.tid || "TID", value: tid },
    { label: I18n.date || "Date", value: formattedDate },
    {
      label: I18n.fee || "Fee",
      value: transactionDetail?.fee
        ? `Rs. ${transactionDetail.fee}`
        : `Rs. 0 ${I18n.fee_free || "(Free)"}`,
    },
    {
      label: I18n.total_amount || "Total amount",
      value: `Rs. ${formatNum(state.amount) || 0}`,
    },
  ];

  const handlePrint = () =>
    printWalletReceiptPreview(
      business,
      {
        headerText: `${I18n.voucher_sent_with || "Voucher sent with"} <strong>${I18n.rupin || "RUPIN"}</strong>`,
        amount: state.amount,
        recipientName: customerName,
        bankInfo: voucherName,
        rows: receiptRows,
      },
      { showSnackbar, setIsPrintLoading, analyticsEvent: "w_earn_vouchers_print" },
    );

  if (isLoading) return <BusinessLoader loadBusiness />;

  return (
    <Fragment>
      {showReceipt ? (
        <UCashTransactionReceipt
          amount={state.amount}
          headerText={
            <>
              {I18n.voucher_sent_with || "Voucher sent with"}{" "}
              <strong>{I18n.rupin || "RUPIN"}</strong>
            </>
          }
          recipientName={customerName}
          bankInfo={voucherName}
          rows={receiptRows}
          onBack={() => setShowReceipt(false)}
          onPrint={handlePrint}
          isPrintLoading={isPrintLoading}
          onCopy={handleCopy}
        />
      ) : (
        <UCashTransactionSuccess
          subtitle={successSubtitle}
          tid={tid}
          amount={state.amount}
          processingTime={"2.6 sec"}
          onViewReceipt={() => setShowReceipt(true)}
          onClose={() => {
            triggerLogEvent(analytics, "w_earn_vouchers_back");
            navigate("/UCash/VoucherTransactions");
          }}
          onDownload={handleDownload}
          isDownloadLoading={isDownloadLoading}
          onBack={() => navigate("/UCash/VoucherTransactions")}
        />
      )}

      <DownloadReceipt
        ref={receiptRef}
        amount={state.amount}
        headerText={I18n.voucher_sent_with_rupin || "Voucher sent with RUPIN"}
        recipientName={customerName}
        bankInfo={voucherName}
        rows={receiptRows}
      />
    </Fragment>
  );
};

export default VouchersTransactionSuccess;
